import React from 'react';
import { useNavigate } from 'react-router-dom';
import type { UserProfile } from '../types/userList.types';
import { Mail, Phone, Eye, Pencil, Trash2 } from 'lucide-react';
import { cn } from '@/utils/cn';
import { ActionMenu } from '@/components/ui/ActionMenu';
import { TablePagination } from '@/components/ui/TablePagination';
import { CardList } from '@/components/ui/CardList';

interface UserTableProps {
 users: UserProfile[];
 selectedId?: string;
 onSelect: (user: UserProfile) => void;
}

export function UserTable({ users, selectedId, onSelect }: UserTableProps) {
 const navigate = useNavigate();

 const getActionItems = (user: UserProfile) => [
 { 
 label: 'Xem chi tiết', 
 icon: Eye, 
 onClick: () => navigate(`/users/${user.id}`) 
 },
 { 
 label: 'Chỉnh sửa', 
 icon: Pencil, 
 onClick: () => navigate(`/users/${user.id}/edit`) 
 },
 { 
 label: 'Xóa nhân sự', 
 icon: Trash2, 
 destructive: true,
 onClick: () => { /* handle delete */ } 
 },
 ];

 const renderMobileCard = (user: UserProfile) => (
 <div 
 key={user.id}
 onClick={() => onSelect(user)}
 className={cn("bg-white rounded-md p-3 border border-slate-100 space-y-2 transition-all",
 selectedId === user.id &&"ring-1 ring-emerald-500 bg-emerald-50/30"
 )}
 >
 <div className="flex items-center justify-between">
 <div className="flex items-center gap-3">
 <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center border border-slate-50 overflow-hidden">
 {user.avatar_path ? (
 <img src={user.avatar_path} alt="" className="w-full h-full object-cover" />
 ) : (
 <span className="sm:text-sm text-xs text-slate-500">{user.full_name[0]}</span>
 )}
 </div>
 <div>
 <div className="sm:text-sm text-xs text-slate-900">{user.full_name}</div>
 <div className="sm:text-sm text-xs text-slate-400">{user.user_code}</div>
 </div>
 </div>
 <ActionMenu items={getActionItems(user)} />
 </div>
 
 <div className="grid grid-cols-2 gap-2 pt-1">
 <div>
 <p className="sm:text-sm text-xs text-slate-400">Chức vụ</p>
 <p className="sm:text-sm text-xs text-slate-600">{user.position_name || 'Nhân viên'}</p>
 </div>
 <div>
 <p className="sm:text-sm text-xs text-slate-400">Trạng thái</p>
 <StatusBadge status={user.status} />
 </div>
 </div>

 <div className="flex items-center gap-4 pt-1">
 <div className="flex items-center gap-1.5 sm:text-sm text-xs text-slate-500">
 <Phone className="w-3 h-3 text-slate-300" /> {user.phone}
 </div>
 <div className="flex items-center gap-1.5 sm:text-sm text-xs text-slate-500">
 <Mail className="w-3 h-3 text-slate-300" /> {user.email}
 </div>
 </div>
 </div>
 );

 return (
 <div className="space-y-4">
 {/* Desktop Table */}
 <div className="hidden md:block bg-white rounded-md border border-slate-100 overflow-hidden">
 <div className="overflow-x-auto">
 <table className="w-full text-left border-collapse">
 <thead>
 <tr className="bg-slate-50/50 border-b border-slate-100">
 <th className="p-3 sm:text-sm text-xs text-slate-500 tracking-wider w-12 text-center font-normal">#</th>
 <th className="p-3 sm:text-sm text-xs text-slate-500 tracking-wider font-normal">Họ và tên</th>
 <th className="p-3 sm:text-sm text-xs text-slate-500 tracking-wider font-normal">Mã nhân sự</th>
 <th className="p-3 sm:text-sm text-xs text-slate-500 tracking-wider font-normal">Chức vụ</th>
 <th className="p-3 sm:text-sm text-xs text-slate-500 tracking-wider font-normal">Phòng ban</th>
 <th className="p-3 sm:text-sm text-xs text-slate-500 tracking-wider font-normal">Liên hệ</th>
 <th className="p-3 sm:text-sm text-xs text-slate-500 tracking-wider font-normal">Trạng thái</th>
 <th className="p-3 sm:text-sm text-xs text-slate-500 tracking-wider text-right font-normal">Thao tác</th>
 </tr>
 </thead>
 <tbody className="divide-y divide-slate-50">
 {users.map((user, i) => (
 <tr 
 key={user.id}
 onClick={() => onSelect(user)}
 className={cn("hover:bg-emerald-50/30 transition-colors cursor-pointer group",
 selectedId === user.id &&"bg-emerald-50/50"
 )}
 >
 <td className="p-3 sm:text-sm text-xs text-slate-400 text-center">
 {(i + 1).toString().padStart(2, '0')}
 </td>
 <td className="p-3">
 <div className="flex items-center gap-3">
 <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center border border-slate-50 overflow-hidden">
 {user.avatar_path ? (
 <img src={user.avatar_path} alt="" className="w-full h-full object-cover" />
 ) : (
 <span className="sm:text-sm text-xs text-slate-500">{user.full_name[0]}</span>
 )}
 </div>
 <div>
 <div className="sm:text-sm text-xs text-slate-900 group-hover:text-emerald-600 transition-colors">{user.full_name}</div>
 <div className="sm:text-sm text-xs text-slate-400">Hệ thống</div>
 </div>
 </div>
 </td>
 <td className="p-3">
 <span className="sm:text-sm text-xs text-slate-600">{user.user_code}</span>
 </td>
 <td className="p-3">
 <span className="sm:text-sm text-xs text-slate-600">{user.position_name || 'Nhân viên'}</span>
 </td>
 <td className="p-3">
 <span className="sm:text-sm text-xs text-slate-500">{user.department_name || '---'}</span>
 </td>
 <td className="p-3">
 <div className="flex flex-col gap-0.5">
 <div className="flex items-center gap-1.5 sm:text-sm text-xs text-slate-500">
 <Phone className="w-3 h-3 text-slate-300" /> {user.phone}
 </div>
 <div className="flex items-center gap-1.5 sm:text-sm text-xs text-slate-500">
 <Mail className="w-3 h-3 text-slate-300" /> {user.email}
 </div>
 </div>
 </td>
 <td className="p-3">
 <StatusBadge status={user.status} />
 </td>
 <td className="p-3 text-right">
 <ActionMenu items={getActionItems(user)} />
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 </div>

 {/* Mobile Card List */}
 <div className="md:hidden">
 <CardList 
 items={users}
 renderCard={renderMobileCard}
 isLoading={false}
 />
 </div>
 
 {/* Shared Pagination */}
 <div className="flex-1">
 <TablePagination 
 currentPage={1}
 totalItems={users.length}
 pageSize={10}
 onPageChange={() => {}}
 />
 </div>
 </div>
 );
}

function StatusBadge({ status }: { status: string }) {
 const configs: any = {
 active: { label: 'Đang làm việc', className: 'bg-emerald-50 text-emerald-600 border-emerald-100' },
 on_leave: { label: 'Nghỉ phép', className: 'bg-amber-50 text-amber-600 border-amber-100' },
 suspended: { label: 'Tạm nghỉ', className: 'bg-indigo-50 text-indigo-600 border-indigo-100' },
 resigned: { label: 'Đã nghỉ việc', className: 'bg-rose-50 text-rose-600 border-rose-100' },
 };
 const config = configs[status] || configs.active;
 return (
 <span className={cn("px-2 py-0.5 rounded-md sm:text-sm text-xs border whitespace-nowrap", config.className)}>
 {config.label}
 </span>
 );
}
